import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { Bell, Mail, BellOff, Loader2 } from "lucide-react";

const CATEGORY_LABELS = {
  dogodki: "Dogodki (koledar)",
  obvestila: "Obvestila (novice)",
  forum: "Forum",
  sporocila: "Osebna sporočila",
  koce: "Koče / rezervacije",
  odstrel: "Odstrel",
  vezice: "Vezice",
};

/**
 * Per-uporabnik drsniki za notifikacije (push+email × 7 kategorij).
 * Prikazano v /profil.
 */
const NotificationPrefs = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/me/notification-prefs`, { headers });
        setPrefs(res.data.prefs);
        setCategories(res.data.categories || []);
      } catch (e) {
        toast.error("Napaka pri nalaganju preferenc");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const toggle = (cat, ch) => {
    setPrefs(p => ({
      ...p,
      [cat]: { ...(p[cat] || {}), [ch]: !p[cat]?.[ch] },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/me/notification-prefs`, { prefs }, { headers });
      toast.success("Preference shranjene");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri shranjevanju");
    } finally {
      setSaving(false);
    }
  };

  const toggleAll = (ch, val) => {
    setPrefs(p => {
      const n = { ...p };
      categories.forEach(cat => {
        n[cat] = { ...(n[cat] || {}), [ch]: val };
      });
      return n;
    });
  };

  if (loading) return (
    <Card><CardContent className="py-8 flex items-center justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </CardContent></Card>
  );

  return (
    <Card data-testid="notification-prefs">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Nastavitve obvestil
        </CardTitle>
        <CardDescription>
          Za vsako kategorijo določite, ali želite prejemati <b>push obvestila</b> in <b>e-mail</b>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Column headers with "all on/off" quick actions */}
        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-6 gap-y-2 text-sm">
          <div className="text-xs text-muted-foreground uppercase font-semibold">Kategorija</div>
          <div className="text-center">
            <div className="flex flex-col items-center">
              <Bell className="h-4 w-4 text-primary" />
              <button
                className="text-[10px] text-primary hover:underline mt-0.5"
                onClick={() => {
                  const allOn = categories.every(c => prefs?.[c]?.push);
                  toggleAll("push", !allOn);
                }}
                data-testid="toggle-all-push"
              >vklopi/izklopi vse</button>
            </div>
          </div>
          <div className="text-center">
            <div className="flex flex-col items-center">
              <Mail className="h-4 w-4 text-primary" />
              <button
                className="text-[10px] text-primary hover:underline mt-0.5"
                onClick={() => {
                  const allOn = categories.every(c => prefs?.[c]?.email);
                  toggleAll("email", !allOn);
                }}
                data-testid="toggle-all-email"
              >vklopi/izklopi vse</button>
            </div>
          </div>

          {categories.map(cat => (
            <div key={cat} className="contents">
              <div className="py-2 border-t">{CATEGORY_LABELS[cat] || cat}</div>
              <div className="py-2 border-t flex justify-center">
                <Switch
                  checked={!!prefs?.[cat]?.push}
                  onCheckedChange={() => toggle(cat, "push")}
                  data-testid={`pref-${cat}-push`}
                />
              </div>
              <div className="py-2 border-t flex justify-center">
                <Switch
                  checked={!!prefs?.[cat]?.email}
                  onCheckedChange={() => toggle(cat, "email")}
                  data-testid={`pref-${cat}-email`}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <BellOff className="h-3 w-3" />
            Nastavitve veljajo takoj po shranjevanju.
          </p>
          <Button onClick={save} disabled={saving} data-testid="save-notification-prefs">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Shrani preference
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationPrefs;
